import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    Output,
    QueryList,
    signal,
    ViewChild,
    ViewChildren,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { scrollIntoView } from "seamless-scroll-polyfill";

import {
    CarouselAppearance,
    CarouselComponent,
    VARIANT_SLIDER,
} from "@hermes/aphrodite/carousel";
import { VtoProduct } from "@hermes/api-model-virtual-try-on";
import {
    LocalizedVtoSubcategory,
    NO_SHADE,
    NO_SUBCATEGORY,
} from "@hermes/states/vto";
import { DOWN, LEFT, RIGHT, UP } from "@hermes/utils-generic/constants";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

import { VirtualTryOnVariantItemComponent } from "../virtual-try-on-variant-item/virtual-try-on-variant-item.component";

@Component({
    selector: "h-virtual-try-on-variants",
    templateUrl: "./virtual-try-on-variants.component.html",
    styleUrls: ["./virtual-try-on-variants.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualTryOnVariantsComponent {
    @Output()
    public selectVtoVariant: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild("noVariant")
    public noVariant!: ElementRef;

    @ViewChild("carousel")
    public hCarousel!: CarouselComponent;

    public isMobile = toSignal(
        this.breakpointService
            .mediumBreakpointObserver()
            .pipe(map(({ matches }) => !matches)),
        { initialValue: false },
    );

    public NO_SHADE = NO_SHADE;

    public hasVariant = false;

    public initialSlide: number = 0;

    public variantSlider: CarouselAppearance = VARIANT_SLIDER;

    public slideTo = signal(-1);

    private selectedVtoSubcategoryInput: LocalizedVtoSubcategory =
        NO_SUBCATEGORY;

    private variantsItemsInput!: QueryList<VirtualTryOnVariantItemComponent>;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: Document,
        private breakpointService: BreakpointService,
        private ngZone: NgZone,
    ) {}

    public get variantsItems(): QueryList<VirtualTryOnVariantItemComponent> {
        return this.variantsItemsInput;
    }

    public get selectedVtoSubcategory() {
        return this.selectedVtoSubcategoryInput;
    }

    @Input({ required: true })
    public set selectedVtoSubcategory(category: LocalizedVtoSubcategory) {
        this.selectedVtoSubcategoryInput = category;
        this.hasVariant = category?.vtoProducts.some(
            (product) => product.sku === category.selectedSkuVariant,
        );

        const activeProductIndex = category.vtoProducts.findIndex(
            (vtoProduct) => vtoProduct.sku === category.selectedSkuVariant,
        );
        this.initialSlide =
            activeProductIndex === -1 ? 1 : activeProductIndex + 1;

        this.changeDetectorRef.detectChanges();

        if (this.hCarousel !== undefined) {
            this.updateSlider();
        }
    }

    @ViewChildren("variantsItems")
    public set variantsItemsSetter(
        items: QueryList<VirtualTryOnVariantItemComponent>,
    ) {
        this.variantsItemsInput = items;
        if (this.isMobile() === false && items.length > 0) {
            const variant = this.getCurrentVariantHTMLElement();
            this.scrollToSelectedVariant(variant);
        }
    }

    // select new sku when slider finish to slide
    public slideChangeTransitionEnd = (realIndex?: number) => {
        this.ngZone.run(() =>
            this.selectVtoVariant.emit(
                this.selectedVtoSubcategory.vtoProducts[
                    (realIndex ?? this.initialSlide) - 1
                ]?.sku ?? NO_SHADE,
            ),
        );
    };

    public getCurrentVariantHTMLElement(): ElementRef<HTMLElement> {
        const productIndex = Math.max(0, this.initialSlide - 1);

        return (
            this.variantsItems.get(productIndex)?.clickZone ?? this.noVariant
        );
    }

    public scrollToSelectedVariant(variant: ElementRef<HTMLElement>) {
        if (variant.nativeElement) {
            scrollIntoView(variant.nativeElement, {
                block: "center",
                inline: "end",
            });
        }
    }

    // modulo help us to know if item start or finish a line of "n" number of items
    // index start from 1
    public getTitleAlignment(index: number): "LEFT" | "RIGHT" | "CENTER" {
        // template has 6 items by line
        const modulo = 6;
        const rest = index % modulo;
        // ex: 1, 7 or 14 items rest 1
        if (rest === 1) {
            return "LEFT";
        }
        // ex: 6, 12 or 18 items rest 0
        if (rest === 0) {
            return "RIGHT";
        }
        return "CENTER";
    }

    public focusAndClick(index: number): void {
        const variant =
            index >= 0 && index < this.variantsItems.length
                ? this.variantsItems.get(index)?.clickZone
                : this.noVariant;

        if (variant && variant.nativeElement !== this.document.activeElement) {
            this.hasVariant = variant !== this.noVariant;
            variant.nativeElement.focus();
            variant.nativeElement.click();
            this.scrollToSelectedVariant(variant);
        }
    }

    public keydownNoVariant(event: KeyboardEvent): void {
        if ([LEFT, UP].includes(event.key)) {
            this.focusAndClick(
                this.selectedVtoSubcategory.vtoProducts.length - 1,
            );
        } else if ([RIGHT, DOWN].includes(event.key)) {
            this.focusAndClick(0);
        }
    }

    public trackByVariantItems(_: number, product: VtoProduct): string {
        return product.sku;
    }

    public keydownVariantNoShade(event: KeyboardEvent): void {
        if (
            [RIGHT, DOWN].includes(event.key) &&
            this.variantsItems.length > 0
        ) {
            this.focusAndClickMobile(this.variantsItems.get(0)?.clickZone, 1);
        }
    }

    public slideToPosition(index: number, position: -1 | 1) {
        if (index + position < 0) {
            this.focusAndClickMobile(this.noVariant, index);
        } else if (index + position <= this.variantsItems.length - 1) {
            this.focusAndClickMobile(
                this.variantsItems.get(index + position)?.clickZone,
                position === -1 ? index : index + 2,
            );
        }
    }

    private focusAndClickMobile(
        element: ElementRef | undefined,
        index: number,
    ): void {
        if (!element) {
            return;
        }
        element.nativeElement.focus();
        element.nativeElement.click();

        this.slideTo.set(index);
        this.changeDetectorRef.detectChanges();
    }

    private updateSlider() {
        if (this.hCarousel.swiper) {
            this.hCarousel.swiper.swiperRef.activeIndex = this.initialSlide;
            this.hCarousel.updateSlides();
        }
    }
}
